import React, { forwardRef, useImperativeHandle, useCallback, useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FieldDefinition } from '../../../../../../types/FieldDefinition';
import { FieldMappingEntry } from '../../../../../../types/DataSyncViewModel';
import { MappingMode } from '../../../../../../types/DataSyncItem';
import { Label } from '@zaiusinc/hera';
import { Button } from '@blueprintjs/core';
import { FieldSelectorDialog } from './FieldSelectorDialog';
import styles from './TreeFieldMappingsTable.module.sass';
import { ValidateHandle } from '../../ValidateHandle';
import { useFieldMappingsState } from '../hooks/useFieldMappingsState';
import { useFieldMappingsData } from '../hooks/useFieldMappingsData';
import { useFieldMappingsHandlers } from '../hooks/useFieldMappingsHandlers';
import { useTreeExpansionEffects } from '../hooks/useTreeExpansionEffects';
import { useTableColumns } from '../hooks/useTableColumns';
import { FieldMappingsTableHeader } from './FieldMappingsTableHeader';
import Notification from '../../../Notification';

export interface TreeFieldMappingsTableProps {
  /** Array of field mapping entries (flat format) */
  viewModelFieldMappings: FieldMappingEntry[];

  /** Source field definitions */
  sourceFields: FieldDefinition[];

  /** Destination field definitions */
  destinationFields: FieldDefinition[];

  /** Callback when mappings change */
  onChange: (updatedFieldMappings: FieldMappingEntry[]) => void;

  /** Field mapping mode - determines which fields are editable */
  fieldMappingMode: MappingMode;

  /** Callback to generate auto-mapped field mappings */
  onAutoMap: () => void;

  /** Source header info (name and icon) */
  sourceInfo?: {
    name: string;
    iconUrl?: string;
  };

  /** Destination header info (name and icon) */
  destinationInfo?: {
    name: string;
    iconUrl?: string;
  };
}

export const TreeFieldMappingsTable = forwardRef<
  ValidateHandle,
  TreeFieldMappingsTableProps
>(
  (
    {
      viewModelFieldMappings,
      sourceFields,
      destinationFields,
      onChange,
      fieldMappingMode,
      onAutoMap,
      sourceInfo,
      destinationInfo
    },
    ref
  ) => {
  const TOOLTIP_STORAGE_KEY = 'fieldMappingsFilterTooltipSeen';
  const [showFilterTooltip, setShowFilterTooltip] = useState(false);
  // Local state for no mappings error
  const [showNoMappingsError, setShowNoMappingsError] = useState(false);

  // State management hook
  const {
    isDialogOpen,
    editingDestinationPath,
    showRequiredHint,
    openDialog,
    closeDialog,
    setShowRequiredHint,
    searchQuery,
    fieldFilter,
    setSearchQuery,
    setFieldFilter,
    expanded,
    userExpanded,
    setExpanded,
    setUserExpanded,
    handleExpandedChange,
    initialExpansionSet,
  } = useFieldMappingsState();

  // Data transformation hook
  const {
    sourceFieldDisplayMap,
    sourceFieldValidations,
    filteredTreeData,
    validationSummary,
  } = useFieldMappingsData({
    sourceFields,
    destinationFields,
    viewModelFieldMappings,
    fieldFilter,
    searchQuery,
  });

  // Event handlers hook
  const {
    handleSelectSourceField,
    handleRemoveMapping,
    handleToggleDisabled,
    currentSourceField,
  } = useFieldMappingsHandlers({
    viewModelFieldMappings,
    fieldMappingMode,
    editingDestinationPath,
    onChange,
    closeDialog,
  });

  // Tree expansion effects
  useTreeExpansionEffects({
    filteredTreeData,
    fieldFilter,
    searchQuery,
    userExpanded,
    initialExpansionSet,
    setExpanded,
    setUserExpanded,
  });

  // Validation imperative handle
  useImperativeHandle(ref, () => ({
    validate: () => {
      // Check if there's at least one complete mapping (both source and destination)
      const hasMappings = viewModelFieldMappings.some(
        (m) => m.sourceField && m.destinationField
      );
      const hasRequiredErrors = validationSummary.unmappedRequiredCount > 0;
      const hasValidationErrors = validationSummary.errorCount > 0;

      setShowNoMappingsError(!hasMappings);
      setShowRequiredHint(hasRequiredErrors);

      return hasMappings && !hasValidationErrors && !hasRequiredErrors;
    },
  }), [viewModelFieldMappings, validationSummary, setShowRequiredHint]);

  // Table columns configuration
  const columns = useTableColumns({
    fieldMappingMode,
    sourceFieldDisplayMap,
    sourceFieldValidations,
    showRequiredHint,
    sourceInfo,
    destinationInfo,
    handlers: {
      onOpenDialog: openDialog,
      onRemoveMapping: handleRemoveMapping,
      onToggleDisabled: handleToggleDisabled,
    },
  });

  const table = useReactTable({
    data: filteredTreeData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => (row.children.length > 0 ? row.children : undefined),
    getRowId: (row) => row.path, // Use stable path as row ID instead of index
    state: {
      expanded,
    },
    onExpandedChange: handleExpandedChange,
  });

  const handleAutoMap = useCallback(() => {
    const hasSeenTooltip = localStorage.getItem(TOOLTIP_STORAGE_KEY);
    if (!hasSeenTooltip) {
      setShowFilterTooltip(true);
    }
    onAutoMap();
  }, [onAutoMap]);

  const handleTooltipDismiss = useCallback(() => {
    localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
    setShowFilterTooltip(false);
  }, []);

  // Compute hasAutoMappedFields from current mappings
  const hasAutoMappedFields = useMemo(() => {
    return viewModelFieldMappings.some((mapping) => mapping.isAutoMapped);
  }, [viewModelFieldMappings]);

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <Label
          label="Field Mappings"
          subLabel="Field mappings connect source fields with matching destination fields."
        >
          <></>
        </Label>
        <Button
          onClick={handleAutoMap}
          intent="primary"
        >
          Auto-map fields
        </Button>
      </div>
      <Notification
        message="Please add at least one field mapping to save this data sync"
        show={showNoMappingsError}
        type="error"
      />

      {/* Search and filter header */}
      <FieldMappingsTableHeader
        searchQuery={searchQuery}
        fieldFilter={fieldFilter}
        onSearchChange={setSearchQuery}
        onFilterChange={setFieldFilter}
        showFilterTooltip={showFilterTooltip}
        onTooltipDismiss={handleTooltipDismiss}
        hasAutoMappedFields={hasAutoMappedFields}
      />

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={styles.tableHeader}
                    style={{ width: header.column.getSize() }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.tableBody}>
            {table.getRowModel().rows.map((row) => {
              const hasChildren = row.original.children && row.original.children.length > 0;
              return (
                <tr
                  key={row.id}
                  className={`${styles.tableRow} ${hasChildren ? styles.tableRowParent : ''}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={styles.tableCell}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Field Selector Dialog */}
      <FieldSelectorDialog
        isOpen={isDialogOpen}
        sourceFields={sourceFields}
        destinationFields={destinationFields}
        destinationFieldPath={editingDestinationPath || ''}
        existingMappings={viewModelFieldMappings}
        selectedField={currentSourceField}
        onSelect={handleSelectSourceField}
        onClose={closeDialog}
      />
    </div>
  );

  }
);

export default TreeFieldMappingsTable;
